import React, { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Stack } from 'react-bootstrap'
import { PostConnectStepFrame } from '../PostConnectStepFrame'
import { PostConnectStepHeader } from '../PostConnectStepHeader'
import { RadioCard } from '../Components/RadioCard'
import { ToggleCard } from '../Components/ToggleCard'
import { useZoomSettings } from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/Zoom/hooks'
import { zoomSettingsDefaultValues, type ZoomSettingsFormValues } from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/Zoom/models'
import { WarningAlert } from '../Components/WarningAlert'
import { POST_CONNECT_COPIES } from '../../copies/onboardingCopies'

export interface ZoomSettingsStepProps {
  onContinue: () => void
  onBack?: () => void
}

export const ZoomSettingsStep: React.FC<ZoomSettingsStepProps> = ({ onContinue, onBack }) => {
  const { zoomIntegration, isAutoJoinEnforced, enforcedMeetingCaptureType, meetingInProgress, isFetching, save } = useZoomSettings()
  const isCaptureTypeDisabled = meetingInProgress || enforcedMeetingCaptureType != null

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<ZoomSettingsFormValues>({
    defaultValues: zoomSettingsDefaultValues as ZoomSettingsFormValues,
  })

  useEffect(() => {
    if (zoomIntegration) {
      const config = zoomIntegration.configuration
      const integrationType = enforcedMeetingCaptureType ?? config.type
      const botConfig = config.type === 'bot' ? config : undefined
      const removeSessionValue = config.type === 'recording' ? (config.removeSessionWhenNoRecording ?? true) : true

      const autoJoinValue = integrationType === 'bot' ? (isAutoJoinEnforced ? botConfig?.enforcedAutoJoin : botConfig?.autoJoin) : undefined

      reset({ integrationType, autoJoin: autoJoinValue, removeSessionWhenNoRecording: removeSessionValue })
    }
  }, [zoomIntegration, reset, isAutoJoinEnforced, enforcedMeetingCaptureType])

  const watchedType = useWatch({ control, name: 'integrationType' })

  const handleSave = handleSubmit(async (data) => {
    await save(data, dirtyFields)
  })

  return (
    <PostConnectStepFrame onSave={handleSave} onAdvance={onContinue} onBack={onBack} loading={isFetching}>
      <PostConnectStepHeader
        icon={<i className='bi bi-check-circle text-success' />}
        title={POST_CONNECT_COPIES['zoom-settings'].headerTitle}
        subtitle={POST_CONNECT_COPIES['zoom-settings'].headerSubtitle}
      />

      <Stack gap={3}>
        <p className='m-0 fw-bold fs-5 text-center text-dark'>Set up Zoom</p>

        <div className='border rounded-4 p-3 d-flex flex-column gap-2'>
          <p className='m-0 fw-bold fs-6 mb-2 text-dark'>Manage how Zocks interacts with your Zoom meetings</p>

          {meetingInProgress ? <WarningAlert>You cannot change your Zoom settings while you have an active meeting in progress on Zoom.</WarningAlert> : null}

          {enforcedMeetingCaptureType ? (
            <div className='d-flex align-items-center gap-3 p-3 rounded-3 zocks-bg-color-primary-light'>
              <i className='bi bi-info-square zocks-text-primary flex-shrink-0' />
              <span className='paragraph-small zocks-text-gray-900'>
                Meeting Capture settings are managed at the enterprise level and cannot be modified here.
              </span>
            </div>
          ) : null}

          <Controller
            name='integrationType'
            control={control}
            render={({ field }) => (
              <Stack gap={2}>
                <RadioCard
                  id='zoom-capture-recording'
                  name='integrationType'
                  value='recording'
                  checked={field.value === 'recording'}
                  disabled={isCaptureTypeDisabled}
                  label="Use Zoom's transcript"
                  description={
                    <>
                      {`Zocks processes Zoom's transcript after the call. `}
                      <strong>No audio is stored.</strong>
                    </>
                  }
                  onChange={field.onChange}
                />
                <RadioCard
                  id='zoom-capture-bot'
                  name='integrationType'
                  value='bot'
                  checked={field.value === 'bot'}
                  disabled={isCaptureTypeDisabled}
                  label='Capture with AI Assistant'
                  description='AI Assistant joins as a participant and transcribes the conversation live'
                  onChange={field.onChange}
                />
              </Stack>
            )}
          />
        </div>

        {watchedType === 'bot' && !isAutoJoinEnforced ? (
          <Controller
            name='autoJoin'
            control={control}
            render={({ field }) => (
              <ToggleCard
                title='Join ad-hoc Zoom meetings'
                description='Your AI Assistant joins calls started directly in Zoom'
                checked={field.value === true}
                disabled={meetingInProgress || isAutoJoinEnforced}
                onChange={field.onChange}
              />
            )}
          />
        ) : null}
      </Stack>
    </PostConnectStepFrame>
  )
}
